import { memo } from 'react';
import type { Group, ChartSettings, Selection } from '../types';

interface GroupNodeProps {
  gcx: number;
  gcy: number;
  discR: number;
  gr: Group;
  gi: number;
  nProgs: number;
  solo: boolean;
  isSelected: boolean;
  onSelect: (selection: Selection) => void;
  settings: ChartSettings;
}

function GroupNode({ gcx, gcy, discR, gr, gi, nProgs, solo, isSelected, onSelect, settings }: GroupNodeProps) {
  const col = gr.color || null;
  const fill = gr.fill || settings.groupFill;
  const txt = gr.textColor || settings.groupText;
  const fsz = gr.fontSize || settings.groupFont;
  const lines = String(gr.label).split('\n');
  const lh = fsz * 1.25;
  const fteText = gr.task || `${gr.programs.reduce((a, p) => a + p.people.length, 0)} FTE`;
  // Selection is derived from this node's own (stable) identity props rather than
  // taking a pre-built object from the parent, so `onSelect` can stay a single
  // stable callback reference from the parent — that's what lets React.memo below
  // actually skip re-rendering unrelated groups during a drag/resize frame.
  const handleSelect = () => onSelect(solo ? { kind: 'prog', gi, pi: 0 } : { kind: 'group', gi });
  return (
    <g
      className={`pnode${isSelected ? ' on' : ''}`}
      data-type="group" data-gi={gi} {...(nProgs === 1 ? { 'data-pi': 0 } : {})}
      tabIndex={0} role="button"
      aria-label={`${gr.short} group. ${nProgs} program(s). Open record.`}
      onClick={handleSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(); } }}
    >
      <circle className="psel" cx={gcx} cy={gcy} r={discR + 12} />
      <circle className="disc-g" cx={gcx} cy={gcy} r={discR}
        style={{ fill, ...(col ? { stroke: col } : {}) }} />
      {lines.map((ln, li) => (
        <text key={li} className="gname" x={gcx}
          style={{ fontSize: `${fsz}px`, fill: txt }}
          y={gcy - (lines.length - 1) * lh / 2 + li * lh + fsz * 0.38}>{ln}</text>
      ))}
      <text className="fte" x={gcx} y={gcy + discR - 12}
        style={gr.badgeColor ? { fill: gr.badgeColor } : undefined}>{fteText}</text>
    </g>
  );
}

export default memo(GroupNode);
